/**
 * <s-app-install> reveals an "Install app" control when the browser can install the store as a web app.
 * - Chromium (Android, Windows, macOS, ChromeOS): captures beforeinstallprompt and opens the native prompt on --install.
 * - iPhone/iPad Safari: no prompt API exists, so --install reveals the Share → Add to Home Screen hint.
 * Stays hidden when already running as an installed app, on a device that has opened the installed app before,
 * or inside in-app browsers that cannot install.
 * Attributes: `dismissible` — remembers a --dismiss on this device (localStorage) and stays hidden afterwards.
 * Commands: --install, --dismiss (buttons with commandfor pointing at this element).
 */
const DISMISS_KEY = 'alqalam-app-install-dismissed';
// Set once the store has been opened as an installed app (or installed) on this device, so the
// browser never offers "Install" again to someone who already has the app.
const INSTALLED_KEY = 'alqalam-app-installed';

const markInstalled = () => {
  try { window.localStorage.setItem(INSTALLED_KEY, '1'); } catch (error) { /* storage unavailable */ }
};
const isMarkedInstalled = () => {
  try { return window.localStorage.getItem(INSTALLED_KEY) === '1'; } catch (error) { return false; }
};

class SAppInstall extends HTMLElement {
  connectedCallback() {
    this.deferredPrompt = null;
    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.matchMedia('(display-mode: minimal-ui)').matches ||
      window.navigator.standalone === true;
    if (standalone) markInstalled();
    if (standalone || isMarkedInstalled() || this.isDismissed()) return;

    const ua = window.navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
    const inAppBrowser = /Instagram|FBAN|FBAV|Line\/|; wv\)/.test(ua);
    const isIOSSafari = isIOS && !/CriOS|FxiOS|EdgiOS/.test(ua) && !inAppBrowser;
    const isAndroid = /Android/.test(ua) && !inAppBrowser;
    this.platform = isIOSSafari ? 'ios' : isAndroid ? 'android' : null;
    if (this.platform === 'ios') this.hidden = false;
    // Android: Chrome only offers installation when the app is NOT installed yet. Wait briefly for that offer;
    // show manual steps only if it never comes and the device has no installed copy of the app.
    if (this.platform === 'android') {
      this.androidTimer = window.setTimeout(async () => {
        if (this.deferredPrompt || isMarkedInstalled() || this.isDismissed()) return;
        try {
          if (navigator.getInstalledRelatedApps) {
            const apps = await navigator.getInstalledRelatedApps();
            if (apps && apps.length) { markInstalled(); return; }
          }
        } catch (error) { /* not supported */ }
        this.hidden = false;
      }, 3500);
    }

    this.onBeforeInstall = (event) => {
      event.preventDefault();
      this.deferredPrompt = event;
      if (!this.isDismissed() && !isMarkedInstalled()) this.hidden = false;
    };
    this.onInstalled = () => {
      this.deferredPrompt = null;
      markInstalled();
      this.hidden = true;
    };
    window.addEventListener('beforeinstallprompt', this.onBeforeInstall);
    window.addEventListener('appinstalled', this.onInstalled);

    this.addEventListener('command', async (event) => {
      if (event.command === '--dismiss') {
        this.hidden = true;
        try {
          window.localStorage.setItem(DISMISS_KEY, '1');
        } catch (error) {
          /* storage unavailable: hide for this page view only */
        }
        return;
      }
      if (event.command !== '--install') return;
      if (this.deferredPrompt) {
        this.deferredPrompt.prompt();
        await this.deferredPrompt.userChoice;
        this.deferredPrompt = null;
        this.hidden = true;
        return;
      }
      const hint = this.querySelector(`[data-install-hint="${this.platform || 'ios'}"]`);
      if (hint) hint.hidden = !hint.hidden;
    });
  }

  isDismissed() {
    if (!this.hasAttribute('dismissible')) return false;
    try {
      return window.localStorage.getItem(DISMISS_KEY) === '1';
    } catch (error) {
      return false;
    }
  }

  disconnectedCallback() {
    if (this.onBeforeInstall) window.removeEventListener('beforeinstallprompt', this.onBeforeInstall);
    if (this.onInstalled) window.removeEventListener('appinstalled', this.onInstalled);
    if (this.androidTimer) window.clearTimeout(this.androidTimer);
  }
}

if (!customElements.get('s-app-install')) customElements.define('s-app-install', SAppInstall);
